const API = process.env.REACT_APP_API_URL || '/api';

function getToken() {
  return localStorage.getItem('token');
}

function setToken(token) {
  if (token) localStorage.setItem('token', token);
  else localStorage.removeItem('token');
}

// ── Active company (tenant) ────────────────────────────────────────
function getActiveCompany() {
  return localStorage.getItem('activeCompanyId') || '';
}
function setActiveCompany(id) {
  if (id) localStorage.setItem('activeCompanyId', id);
  else localStorage.removeItem('activeCompanyId');
}

// Headers used by multipart/file requests (no JSON content-type).
function authHeaders() {
  const headers = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const cid = getActiveCompany();
  if (cid) headers['X-Company-Id'] = cid;
  return headers;
}

// Build a query string, dropping undefined/null/'' values so they don't get
// serialized as the literal string "undefined" (which breaks server filters).
function buildQuery(params = {}) {
  const clean = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') clean[k] = v;
  }
  const q = new URLSearchParams(clean).toString();
  return q ? `?${q}` : '';
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const cid = getActiveCompany();
  if (cid) headers['X-Company-Id'] = cid;

  const res = await fetch(`${API}${path}`, { ...options, headers });

  // Auto-logout on 401
  if (res.status === 401 && !path.includes('/auth/login')) {
    setToken(null);
    window.location.href = '/';
    throw new Error('Session expired');
  }

  const data = await res.json();
  if (!res.ok) {
    const err = new Error(data.error || 'Request failed');
    err.code = data.code;          // e.g. 'CAPACITY_FULL'
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  // Auth
  login: (email, password) =>
    request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  signup: (data) => request('/auth/signup', { method: 'POST', body: JSON.stringify(data) }),
  getMe: () => request('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    request('/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }),
  forgotPassword: (email) =>
    request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token, newPassword) =>
    request('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, newPassword }) }),

  // Token helpers
  setToken,
  getToken,
  clearToken: () => setToken(null),

  // Active company (tenant) helpers
  getActiveCompany,
  setActiveCompany,
  clearActiveCompany: () => setActiveCompany(null),
  getCompanies: () => request('/companies'),
  getCompaniesOverview: () => request('/companies/overview'),

  // Plan / billing
  getPlan: () => request('/plan'),
  subscribe: (planType, seats) => request('/plan/subscribe', { method: 'POST', body: JSON.stringify({ planType, seats }) }),
  syncSubscription: () => request('/plan/subscription/sync', { method: 'POST' }),
  finalizeSubscription: (data) => request('/plan/subscription/finalize', { method: 'POST', body: JSON.stringify(data) }),
  abandonSubscription: () => request('/plan/subscription/abandon', { method: 'POST' }),
  setAutoRenew: (enabled) => request('/plan/auto-renew', { method: 'POST', body: JSON.stringify({ enabled }) }),
  addPlanSlots: (count) => request('/plan/add-slots', { method: 'POST', body: JSON.stringify({ count }) }),
  removePlanSlots: (count) => request('/plan/remove-slots', { method: 'POST', body: JSON.stringify({ count }) }),

  // Users
  getUsers: (params = {}) => request(`/users${buildQuery(params)}`),
  getUser: (id) => request(`/users/${id}`),
  createUser: (data) => request('/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteUser: (id) => request(`/users/${id}`, { method: 'DELETE' }),
  resetUserPassword: (id, newPassword) =>
    request(`/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify({ newPassword }) }),

  // Leads
  getLeads: (params = {}) => request(`/leads${buildQuery(params)}`),
  getLead: (id) => request(`/leads/${id}`),
  createLead: (data) => request('/leads', { method: 'POST', body: JSON.stringify(data) }),
  updateLead: (id, data) => request(`/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLead: (id) => request(`/leads/${id}`, { method: 'DELETE' }),

  // Activities
  getActivities: (leadId) => request(`/activities/lead/${leadId}`),
  createActivity: (data) => request('/activities', { method: 'POST', body: JSON.stringify(data) }),
  deleteActivity: (id) => request(`/activities/${id}`, { method: 'DELETE' }),

  // Settings
  getSettings: () => request('/settings'),
  updateSettings: (data) => request('/settings', { method: 'PUT', body: JSON.stringify(data) }),

  // Email
  sendEmail: (data) => request('/email/send', { method: 'POST', body: JSON.stringify(data) }),
  previewEmail: (data) => request('/email/preview', { method: 'POST', body: JSON.stringify(data) }),
  testSmtp: () => request('/email/test', { method: 'POST' }),
  getBroadcastRecipients: () => request('/email/broadcast/recipients'),
  sendBroadcast: async ({ subject, htmlBody, textBody, attachments = [], recipientIds }) => {
    const formData = new FormData();
    formData.append('subject', subject);
    formData.append('htmlBody', htmlBody);
    if (textBody) formData.append('textBody', textBody);
    if (recipientIds) formData.append('recipientIds', JSON.stringify(recipientIds));
    for (const file of attachments) formData.append('attachments', file);
    const res = await fetch(`${API}/email/broadcast`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Broadcast failed');
    return data;
  },

  // Email Templates
  getEmailTemplates: () => request('/email-templates'),
  getEmailTemplate: (key) => request(`/email-templates/${key}`),
  updateEmailTemplate: (key, data) => request(`/email-templates/${key}`, { method: 'PUT', body: JSON.stringify(data) }),
  resetEmailTemplate: (key) => request(`/email-templates/${key}/reset`, { method: 'POST' }),
  getEmployerDeck: () => request('/email-templates/assets/employer-deck'),
  uploadEmployerDeck: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API}/email-templates/assets/employer-deck`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return data;
  },
  resetEmployerDeck: () => request('/email-templates/assets/employer-deck', { method: 'DELETE' }),
  downloadEmployerDeck: async () => {
    const res = await fetch(`${API}/email-templates/assets/employer-deck/download`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'TJ_Employer_Perk_Program.pdf';
    a.click();
    window.URL.revokeObjectURL(url);
  },
  getSlickSheet: () => request('/email-templates/assets/slick-sheet'),
  uploadSlickSheet: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API}/email-templates/assets/slick-sheet`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return data;
  },
  resetSlickSheet: () => request('/email-templates/assets/slick-sheet', { method: 'DELETE' }),
  downloadSlickSheet: async () => {
    const res = await fetch(`${API}/email-templates/assets/slick-sheet/download`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'TJ_Perk_Plan_Slick_Sheet.pdf';
    a.click();
    window.URL.revokeObjectURL(url);
  },

  // Documents (marketing materials per lead)
  getDocuments: (leadId) => request(`/leads/${leadId}/documents`),
  uploadDocuments: async (leadId, files) => {
    const formData = new FormData();
    for (const file of files) formData.append('files', file);
    const res = await fetch(`${API}/leads/${leadId}/documents`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return data;
  },
  deleteDocument: (leadId, docId) =>
    request(`/leads/${leadId}/documents/${docId}`, { method: 'DELETE' }),
  getDocumentUrl: (leadId, docId) =>
    `${API}/leads/${leadId}/documents/${docId}/download`,

  // Company Logo (per lead)
  getLogoInfo: (leadId) => request(`/leads/${leadId}/logo`),
  getLogoUrl: (leadId) => `${API}/leads/${leadId}/logo/view?token=${getToken()}&companyId=${getActiveCompany()}`,
  uploadLogo: async (leadId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API}/leads/${leadId}/logo`, {
      method: 'POST',
      headers: authHeaders(),
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Upload failed');
    return data;
  },
  downloadLogo: async (leadId, filename) => {
    const res = await fetch(`${API}/leads/${leadId}/logo/download`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'company_logo';
    a.click();
    window.URL.revokeObjectURL(url);
  },
  deleteLogo: (leadId) => request(`/leads/${leadId}/logo`, { method: 'DELETE' }),

  // Assignable users (lightweight list for dropdowns)
  getAssignableUsers: () => request('/users/assignable'),

  // Franchisees
  getFranchisees: () => request('/franchisees'),
  getFranchisee: (id) => request(`/franchisees/${id}`),
  createFranchisee: (data) => request('/franchisees', { method: 'POST', body: JSON.stringify(data) }),
  updateFranchisee: (id, data) => request(`/franchisees/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteFranchisee: (id) => request(`/franchisees/${id}`, { method: 'DELETE' }),

  // Clinic Locations (nested under franchisee)
  addClinic: (franchiseeId, data) => request(`/franchisees/${franchiseeId}/clinics`, { method: 'POST', body: JSON.stringify(data) }),
  updateClinic: (franchiseeId, clinicId, data) => request(`/franchisees/${franchiseeId}/clinics/${clinicId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteClinic: (franchiseeId, clinicId) => request(`/franchisees/${franchiseeId}/clinics/${clinicId}`, { method: 'DELETE' }),

  // Dashboard
  getDashboard: () => request('/dashboard'),
};
